import axios from 'axios';
import { cacheClearAll } from './offlineCache';
import { getApiUrl } from '../utils';
import { getRefreshToken, setRefreshToken, clearRefreshToken } from './secureStorage';

const AUTH_EXEMPT_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/google',
  '/auth/apple',
  '/auth/social-status',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/google/redirect',
  '/auth/google/callback',
  '/auth/refresh',
  '/auth/logout'
];

let interceptorsInstalled = false;
let handlingUnauthorized = false;
let refreshPromise = null;

function normalizeUrl(url) {
  if (!url) return '';
  return typeof url === 'string' ? url : url.url || '';
}

function isApiRequest(url) {
  return normalizeUrl(url).includes('/api/');
}

function isAuthExempt(url) {
  const normalized = normalizeUrl(url);
  return AUTH_EXEMPT_PATHS.some((path) => normalized.includes(path));
}

async function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  await clearRefreshToken();
  await cacheClearAll();
}

// Only one /auth/refresh call is ever in flight — concurrent 401s
// (several requests failing around the same time) share this promise
// instead of each triggering their own refresh.
function refreshAccessToken(originalFetch) {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = await getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const res = await originalFetch(`${getApiUrl()}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (!res.ok) {
        throw new Error('Refresh token invalid or expired');
      }

      const data = await res.json();
      localStorage.setItem('token', data.token);
      await setRefreshToken(data.refreshToken);
      return data.token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function forceLogout() {
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;
  await clearSession();

  const target = '/login?session_expired=1';
  if (window.location.pathname !== '/login' || window.location.search !== '?session_expired=1') {
    window.location.assign(target);
    return;
  }

  handlingUnauthorized = false;
}

// Attempts a silent refresh for a 401 on a real, non-exempt, authenticated
// API request. Returns the new access token on success, or null if a
// refresh wasn't applicable/possible (forcing a logout itself if it was
// applicable but failed).
async function tryRefreshFor(url, originalFetch) {
  if (!isApiRequest(url) || isAuthExempt(url) || !localStorage.getItem('token')) {
    return null;
  }
  try {
    return await refreshAccessToken(originalFetch);
  } catch (refreshError) {
    await forceLogout();
    return null;
  }
}

function setAuthHeader(headers, token) {
  if (headers && typeof headers.set === 'function') {
    headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }
  return { ...headers, Authorization: `Bearer ${token}` };
}

export function setupApiAuthHandling() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  const originalFetch = window.fetch.bind(window);

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      const status = error?.response?.status;

      if (status === 401 && config) {
        if (config._retriedAfterRefresh) {
          // Already retried once with a fresh token and still unauthorized — real failure.
          await forceLogout();
        } else {
          const newToken = await tryRefreshFor(config.url, originalFetch);
          if (newToken) {
            config._retriedAfterRefresh = true;
            config.headers = setAuthHeader(config.headers, newToken);
            return axios(config);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  window.fetch = async (input, init = {}) => {
    const response = await originalFetch(input, init);

    if (response.status === 401) {
      if (init.__retriedAfterRefresh) {
        await forceLogout();
      } else {
        const newToken = await tryRefreshFor(input, originalFetch);
        if (newToken) {
          const retryInit = {
            ...init,
            __retriedAfterRefresh: true,
            headers: setAuthHeader(init.headers || {}, newToken)
          };
          return originalFetch(input, retryInit);
        }
      }
    }

    return response;
  };
}
